/**
 * HeroCarousel Component - Cinematic Hero Design
 *
 * Features:
 * - Blurred thumbnail as immersive background
 * - Large 3D perspective book cover
 * - Story description preview
 * - Slide-specific color themes
 * - Smooth transitions and hover effects
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, TrendingUp, BookOpen, Library, Clock, Crown, ArrowRight, Heart, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const HeroCarousel = () => {
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [trendingStory, setTrendingStory] = useState(null);
  const [newStory, setNewStory] = useState(null);
  const [proStory, setProStory] = useState(null);
  const [featuredSeries, setFeaturedSeries] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [imageLoaded, setImageLoaded] = useState({});

  // Load data for slides
  useEffect(() => {
    const loadSlideData = async () => {
      try {
        // Load trending story
        const trendingRes = await axios.get(`${API}/stories/featured?limit=1`, {
          withCredentials: true
        });
        if (trendingRes.data?.[0]) {
          setTrendingStory(trendingRes.data[0]);
        }

        // Load newest story
        const newRes = await axios.get(`${API}/stories/explore?sort=new&limit=1`, {
          withCredentials: true
        });
        if (newRes.data?.[0]) {
          setNewStory(newRes.data[0]);
        }

        // Load pro story
        const proRes = await axios.get(`${API}/stories/explore?pro_filter=pro&sort=trending&limit=1`, {
          withCredentials: true
        });
        if (proRes.data?.[0]) {
          setProStory(proRes.data[0]);
        }

        // Load featured series (most popular)
        const seriesRes = await axios.get(`${API}/v1/series/public/explore?sort=popular&limit=1`, {
          withCredentials: true
        });
        if (seriesRes.data?.series?.[0]) {
          setFeaturedSeries(seriesRes.data.series[0]);
        }
      } catch (error) {
        console.error('Failed to load carousel data:', error);
      }
    };

    loadSlideData();
  }, []);

  // Auto-rotate slides
  useEffect(() => {
    if (isHovered || isPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 4);
    }, 6000); // Slightly longer for reading description

    return () => clearInterval(interval);
  }, [isHovered, isPaused]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        setCurrentSlide((prev) => (prev - 1 + 4) % 4);
        break;
      case 'ArrowRight':
        e.preventDefault();
        setCurrentSlide((prev) => (prev + 1) % 4);
        break;
      case ' ':
        e.preventDefault();
        setIsPaused((prev) => !prev);
        break;
      default:
        break;
    }
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % 4);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + 4) % 4);
  }, []);

  // Slide configurations with enhanced theming
  // Using darker overlays for better text contrast in both light and dark modes
  const slides = [
    {
      id: 'trending',
      badge: 'Trending This Week',
      badgeIcon: TrendingUp,
      type: 'story',
      data: trendingStory,
      fallbackTitle: 'Discover Trending Stories',
      fallbackSubtitle: 'See what the community is reading',
      fallbackDescription: 'Explore the most popular stories loved by families everywhere.',
      ctaText: trendingStory ? 'Read Story' : 'Explore',
      ctaAction: () => trendingStory ? navigate(`/story/${trendingStory.id}`) : navigate('/explore'),
      accentColor: '#FF8C61',
      gradientOverlay: 'linear-gradient(135deg, rgba(30,20,15,0.9) 0%, rgba(30,20,15,0.7) 40%, rgba(30,20,15,0.3) 100%)',
      badgeBg: 'rgba(255,140,97,0.2)',
    },
    {
      id: 'new',
      badge: 'Just Published',
      badgeIcon: Clock,
      type: 'story',
      data: newStory,
      fallbackTitle: 'Fresh Stories Await',
      fallbackSubtitle: 'Be the first to discover new adventures',
      fallbackDescription: 'New magical tales added every day by creative storytellers.',
      ctaText: newStory ? 'Read Story' : 'Browse New',
      ctaAction: () => newStory ? navigate(`/story/${newStory.id}`) : navigate('/home?filter=new'),
      accentColor: '#10B981',
      gradientOverlay: 'linear-gradient(135deg, rgba(5,40,30,0.9) 0%, rgba(5,40,30,0.7) 40%, rgba(5,40,30,0.3) 100%)',
      badgeBg: 'rgba(16,185,129,0.2)',
    },
    {
      id: 'series',
      badge: 'Featured Series',
      badgeIcon: Library,
      type: 'series',
      data: featuredSeries,
      fallbackTitle: 'Explore Story Series',
      fallbackSubtitle: 'Follow connected adventures',
      fallbackDescription: 'Dive into multi-book adventures with consistent characters.',
      ctaText: featuredSeries ? 'View Series' : 'Browse Series',
      ctaAction: () => featuredSeries ? navigate(`/series/public/${featuredSeries.id}`) : navigate('/explore?tab=series'),
      accentColor: '#818CF8',
      gradientOverlay: 'linear-gradient(135deg, rgba(30,27,75,0.9) 0%, rgba(30,27,75,0.7) 40%, rgba(30,27,75,0.3) 100%)',
      badgeBg: 'rgba(99,102,241,0.2)',
    },
    {
      id: 'pro',
      badge: 'Pro Quality',
      badgeIcon: Crown,
      type: 'story',
      data: proStory,
      fallbackTitle: 'Premium AI Illustrations',
      fallbackSubtitle: 'Stunning visuals with consistent characters',
      fallbackDescription: 'Experience the highest quality AI-generated artwork.',
      ctaText: proStory ? 'View Story' : 'Explore Pro',
      ctaAction: () => proStory ? navigate(`/story/${proStory.id}`) : navigate('/home?filter=pro'),
      accentColor: '#A78BFA',
      gradientOverlay: 'linear-gradient(135deg, rgba(45,20,80,0.9) 0%, rgba(45,20,80,0.7) 40%, rgba(45,20,80,0.3) 100%)',
      badgeBg: 'rgba(139,92,246,0.2)',
    }
  ];

  return (
    <div
      ref={carouselRef}
      className="relative w-full mb-5 group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured content carousel"
    >
      {/* Screen reader live region */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        Slide {currentSlide + 1} of {slides.length}: {slides[currentSlide]?.badge}
      </div>

      {/* Main Carousel Container */}
      <div
        className="rounded-2xl overflow-hidden shadow-lg focus-within:ring-2 focus-within:ring-orange-300"
        style={{ border: '1px solid var(--st-border-light)' }}
      >
        {/* Increased height for cinematic feel */}
        <div className="relative h-[240px] sm:h-[280px] md:h-[320px]">
          {slides.map((slide, index) => {
            const BadgeIcon = slide.badgeIcon;
            const data = slide.data;
            const isActive = currentSlide === index;
            const isSeries = slide.type === 'series';

            // Get content
            const title = data?.title || slide.fallbackTitle;
            const subtitle = isSeries
              ? (data ? `${data.book_count || 0} books • by ${data.author_name || 'Anonymous'}` : slide.fallbackSubtitle)
              : (data ? `by ${data.author_name || 'Anonymous'}` : slide.fallbackSubtitle);

            // Get description - use story summary or fallback
            const description = data?.summary || data?.description || slide.fallbackDescription;

            // Get thumbnail
            const thumbnail = isSeries
              ? (data?.cover_image_url || data?.book_summaries?.[0]?.thumbnail)
              : data?.thumbnail;

            const thumbnailUrl = getImageUrl(thumbnail);

            // Get stats
            const pageCount = data?.page_count;
            const viewCount = data?.views_count;
            const favoriteCount = data?.favorites_count;

            return (
              <div
                key={slide.id}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${slides.length}: ${slide.badge}`}
                aria-hidden={!isActive}
                className={`absolute inset-0 transition-all duration-700 ease-out ${
                  isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
                style={{ pointerEvents: isActive ? 'auto' : 'none' }}
              >
                {/* Layer 1: Blurred Background Image */}
                <div className="absolute inset-0 overflow-hidden">
                  {thumbnailUrl ? (
                    <>
                      <img
                        src={thumbnailUrl}
                        alt=""
                        aria-hidden="true"
                        className="absolute inset-0 w-full h-full object-cover scale-125 transition-opacity duration-500"
                        style={{
                          filter: 'blur(40px) saturate(1.3) brightness(0.9)',
                          opacity: imageLoaded[slide.id] ? 0.7 : 0,
                        }}
                        onLoad={() => setImageLoaded(prev => ({ ...prev, [slide.id]: true }))}
                      />
                      {/* Fallback gradient while image loads */}
                      <div
                        className="absolute inset-0 transition-opacity duration-500"
                        style={{
                          background: `linear-gradient(135deg, ${slide.accentColor}30 0%, ${slide.accentColor}10 100%)`,
                          opacity: imageLoaded[slide.id] ? 0 : 1,
                        }}
                      />
                    </>
                  ) : (
                    <div
                      className="absolute inset-0"
                      style={{
                        background: `linear-gradient(135deg, ${slide.accentColor}20 0%, ${slide.accentColor}05 100%)`,
                      }}
                    />
                  )}
                </div>

                {/* Layer 2: Gradient Overlay for text readability */}
                <div
                  className="absolute inset-0"
                  style={{ background: slide.gradientOverlay }}
                />

                {/* Layer 3: Content */}
                <div className="relative h-full flex">
                  {/* Left Content Side */}
                  <div className="flex-1 p-4 sm:p-6 md:p-8 flex flex-col justify-center max-w-[60%] sm:max-w-[65%] md:max-w-[60%]">
                    {/* Badge */}
                    <div
                      className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-bold mb-2 sm:mb-3 w-fit backdrop-blur-sm"
                      style={{
                        background: slide.badgeBg,
                        color: slide.accentColor,
                        border: `1px solid ${slide.accentColor}30`,
                      }}
                    >
                      <BadgeIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                      {slide.badge}
                    </div>

                    {/* Title */}
                    <h2
                      className="text-base sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-1.5 line-clamp-1 sm:line-clamp-2"
                      style={{ fontFamily: 'Chewy, cursive', color: '#FFFFFF', textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}
                    >
                      {title}
                    </h2>

                    {/* Subtitle/Author + Stats */}
                    <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2 flex-wrap">
                      <p
                        className="text-xs sm:text-sm line-clamp-1"
                        style={{ fontFamily: 'Quicksand, sans-serif', color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}
                      >
                        {subtitle}
                      </p>
                      {pageCount && (
                        <span
                          className="hidden sm:inline text-xs px-2 py-0.5 rounded-full opacity-80"
                          style={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontFamily: 'Quicksand, sans-serif' }}
                        >
                          {pageCount} pages
                        </span>
                      )}
                    </div>

                    {/* Description - Hidden on small mobile */}
                    <p
                      className="hidden sm:block text-sm mb-4 line-clamp-2"
                      style={{ fontFamily: 'Quicksand, sans-serif', color: 'rgba(255,255,255,0.85)', textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                    >
                      {description}
                    </p>

                    {/* Stats Row - Hidden on mobile */}
                    {(viewCount > 0 || favoriteCount > 0) && (
                      <div className="hidden sm:flex items-center gap-4 mb-4 text-xs" style={{ color: 'rgba(255,255,255,0.75)', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                        {viewCount > 0 && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-3.5 h-3.5" />
                            {viewCount} views
                          </span>
                        )}
                        {favoriteCount > 0 && (
                          <span className="flex items-center gap-1">
                            <Heart className="w-3.5 h-3.5" />
                            {favoriteCount} favorites
                          </span>
                        )}
                      </div>
                    )}

                    {/* CTA Buttons */}
                    <div className="flex items-center gap-2 sm:gap-3 mt-2 sm:mt-0">
                      {/* Primary CTA */}
                      <button
                        onClick={slide.ctaAction}
                        className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:scale-[1.03] active:scale-[0.98]"
                        style={{
                          background: slide.accentColor,
                          color: 'white',
                          fontFamily: 'Quicksand, sans-serif',
                          boxShadow: `0 4px 20px ${slide.accentColor}50`,
                        }}
                      >
                        {slide.ctaText}
                        <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>

                      {/* Secondary CTA - Only for stories with data */}
                      {data && !isSeries && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Navigate to story page (favorite action would need auth)
                            navigate(`/story/${data.id}`);
                          }}
                          className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 hover:bg-white/20"
                          style={{
                            background: 'rgba(255,255,255,0.1)',
                            color: 'white',
                            fontFamily: 'Quicksand, sans-serif',
                            border: '1px solid rgba(255,255,255,0.3)',
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          <Heart className="w-4 h-4" />
                          Save
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Right Side - 3D Book Cover */}
                  <div className="flex items-center justify-center pr-4 sm:pr-6 md:pr-10">
                    {thumbnailUrl ? (
                      <div
                        className="relative cursor-pointer transition-all duration-500 hover:scale-105 group/book"
                        onClick={slide.ctaAction}
                        style={{ perspective: '1000px' }}
                      >
                        {/* Book cover with 3D effect */}
                        <div
                          className="w-[100px] h-[133px] sm:w-[140px] sm:h-[186px] md:w-[180px] md:h-[240px] rounded-lg sm:rounded-xl overflow-hidden transition-all duration-500 group-hover/book:shadow-2xl"
                          style={{
                            transform: 'rotateY(-8deg)',
                            boxShadow: `
                              0 25px 50px rgba(0,0,0,0.4),
                              0 10px 20px rgba(0,0,0,0.3),
                              -8px 0 20px rgba(0,0,0,0.2)
                            `,
                          }}
                        >
                          <img
                            src={thumbnailUrl}
                            alt={title}
                            className="w-full h-full object-cover"
                          />

                          {/* Shine effect on hover */}
                          <div
                            className="absolute inset-0 opacity-0 group-hover/book:opacity-100 transition-opacity duration-500 pointer-events-none"
                            style={{
                              background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 50%, transparent 100%)',
                            }}
                          />
                        </div>

                        {/* Book spine shadow effect */}
                        <div
                          className="absolute left-0 top-2 bottom-2 w-2 rounded-l-sm"
                          style={{
                            background: 'linear-gradient(to right, rgba(0,0,0,0.3), transparent)',
                            transform: 'rotateY(-8deg)',
                          }}
                        />

                        {/* Bottom reflection */}
                        <div
                          className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-[80%] h-4 rounded-full blur-md opacity-30"
                          style={{ background: slide.accentColor }}
                        />
                      </div>
                    ) : (
                      <div
                        className="w-[100px] h-[133px] sm:w-[140px] sm:h-[186px] md:w-[180px] md:h-[240px] rounded-lg sm:rounded-xl flex items-center justify-center"
                        style={{
                          background: 'rgba(255,255,255,0.1)',
                          backdropFilter: 'blur(8px)',
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                      >
                        {isSeries ? (
                          <Library className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 opacity-50" style={{ color: 'white' }} />
                        ) : (
                          <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 opacity-50" style={{ color: 'white' }} />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            aria-label="Previous slide"
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all duration-300 hover:bg-white/30 hover:scale-110 focus:ring-2 focus:ring-white/50 focus:outline-none z-20"
            style={{ border: '1px solid rgba(255,255,255,0.3)' }}
          >
            <ChevronLeft className="w-5 h-5 text-white" aria-hidden="true" />
          </button>
          <button
            onClick={nextSlide}
            aria-label="Next slide"
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all duration-300 hover:bg-white/30 hover:scale-110 focus:ring-2 focus:ring-white/50 focus:outline-none z-20"
            style={{ border: '1px solid rgba(255,255,255,0.3)' }}
          >
            <ChevronRight className="w-5 h-5 text-white" aria-hidden="true" />
          </button>

          {/* Dot Indicators - Hidden on mobile */}
          <div
            className="hidden sm:flex absolute bottom-4 left-1/2 -translate-x-1/2 items-center gap-2 z-20 px-3 py-1.5 rounded-full backdrop-blur-sm"
            style={{ background: 'rgba(0,0,0,0.2)' }}
            role="tablist"
            aria-label="Carousel navigation"
          >
            {slides.map((slide, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                role="tab"
                aria-selected={currentSlide === index}
                aria-label={`Go to slide ${index + 1}: ${slide.badge}`}
                className="transition-all p-0.5 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full"
              >
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index ? 'w-6' : 'w-2'
                  }`}
                  style={{
                    background: currentSlide === index
                      ? slide.accentColor
                      : 'rgba(255,255,255,0.5)',
                  }}
                  aria-hidden="true"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroCarousel;
